import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  pending2FA: { email: string } | null;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null; needsConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; needs2FA: boolean }>;
  verify2FA: (token: string) => Promise<{ error: Error | null }>;
  cancel2FA: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState<{ email: string } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = window.location.hostname === 'localhost' 
      ? `${window.location.origin}/` 
      : 'https://gaius-tech.com/';
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Erreur d'inscription",
        description: error.message,
      });
      return { error, needsConfirmation: false };
    }

    const needsConfirmation = !data.session && !!data.user;
    
    if (needsConfirmation) {
      toast({
        title: "Vérifiez votre boîte mail",
        description: "Un email de confirmation vous a été envoyé. Cliquez sur le lien pour activer votre compte.",
      });
    } else {
      toast({
        title: "Inscription réussie !",
        description: "Bienvenue sur Gaius.",
      });
    }
    
    return { error: null, needsConfirmation };
  };

  const signIn = async (email: string, password: string) => {
    // Step 1: Verify password
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: error.message,
      });
      return { error, needs2FA: false };
    }
    
    // Step 2: Sign out immediately and send OTP for 2FA
    await supabase.auth.signOut();
    
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });
    
    if (otpError) {
      toast({
        variant: "destructive",
        title: "Erreur d'envoi du code",
        description: otpError.message,
      });
      return { error: otpError, needs2FA: false };
    }
    
    setPending2FA({ email });
    toast({
      title: "Code de vérification envoyé",
      description: "Un code de vérification a été envoyé à votre adresse email.",
    });
    
    return { error: null, needs2FA: true };
  };

  const verify2FA = async (token: string) => {
    if (!pending2FA) {
      return { error: new Error('No pending 2FA') };
    }
    
    const { error } = await supabase.auth.verifyOtp({
      email: pending2FA.email,
      token,
      type: 'email',
    });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Code invalide",
        description: "Le code de vérification est incorrect ou a expiré.",
      });
      return { error };
    }
    
    setPending2FA(null);
    toast({
      title: "Connexion réussie !",
      description: "Bienvenue sur Gaius.",
    });
    
    return { error: null };
  };

  const cancel2FA = () => {
    setPending2FA(null);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Déconnexion",
      description: "À bientôt !",
    });
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, pending2FA, signUp, signIn, verify2FA, cancel2FA, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
